/**
 * Evidence tagging — what a clinician can tag a referral answer as: a
 * criterion, a risk, an other factor, or something to add to their conclusion.
 *
 * Tagging is a clinician labelling an answer they have read: "this line is the
 * evidence for A2". It never computes, infers or suggests anything, and the
 * tag has no bearing on the decision beyond being the clinician's own note of
 * where their reasoning came from.
 *
 * Both pathways tag against the criteria their own decision wizard grades, so
 * a tag made while reading the proforma lands on the criterion the clinician
 * will later answer:
 *   - autism: the seven DSM-5 A/B criteria
 *   - ADHD:   the five graded evidence questions
 *
 * Criteria C and D (autism) and the ADHD comment-only fields are deliberately
 * absent: they are judgements about the whole picture, not lines to point at.
 *
 * Keep the key sets in sync with TAG_CRITERION_KEYS_BY_PATHWAY in
 * python-functions/rms_functions/rms_forms.py.
 */

import {
  AUTISM_OTHER_FACTOR_GROUPS,
  AUTISM_TAG_CRITERION_OPTIONS,
  AUTISM_TAG_SOURCE_OPTIONS,
} from './autismTriageOutcomes';
import { EVIDENCE_QUESTIONS, OTHER_FACTOR_GROUPS } from './adhdTriageOutcomes';
import { PATHWAY } from '../utils/triagePathways';

/**
 * What a tag says about the line it points at (TDI-394, Tom King's ruling R1,
 * 27 October 2026). The clinician picks the kind; nothing is inferred:
 *   - criterion:   evidence for one of the pathway's criteria
 *   - risk:        a risk, shown on the decision's risks and factors screen
 *   - otherFactor: one of the pathway's other-factor groups, optionally a
 *                  specific factor within it
 *   - conclusion:  something the clinician wants beside their conclusion
 *
 * Keep in sync with TAG_KINDS in python-functions/rms_functions/rms_forms.py.
 */
export const TAG_KIND = {
  CRITERION: 'criterion',
  RISK: 'risk',
  OTHER_FACTOR: 'otherFactor',
  CONCLUSION: 'conclusion',
};

/** The four kinds, in the order the tag menu offers them. */
export const TAG_KIND_OPTIONS = [
  { id: TAG_KIND.CRITERION, label: 'Criterion' },
  { id: TAG_KIND.RISK, label: 'Risk' },
  { id: TAG_KIND.OTHER_FACTOR, label: 'Other factor' },
  { id: TAG_KIND.CONCLUSION, label: 'Add to my conclusion' },
];

/** The longest note a tag may carry, the same ceiling as the tagged text. */
export const TAG_NOTE_MAX_CHARS = 500;

/**
 * A tag's kind. A tag stored before kinds existed was always a criterion tag,
 * so it reads as one.
 */
export function tagKindOf(tag) {
  return tag?.kind || TAG_KIND.CRITERION;
}

/** The pathway's tags of one kind, in the order they were made. */
export function tagsOfKind(tags, kind) {
  return (tags || []).filter((tag) => tagKindOf(tag) === kind);
}

/** Short codes for the ADHD evidence questions, for the tag chips. */
const ADHD_TAG_CODES = {
  settingsAcrossTwoOrMore: 'Settings',
  onsetBeforeAge12: 'Onset',
  functionalImpairment: 'Impairment',
  snapConsistency: 'SNAP',
  schoolReasonableAdjustments: 'Adjustments',
};

export const ADHD_TAG_CRITERION_OPTIONS = EVIDENCE_QUESTIONS.map((q) => ({
  key: q.key,
  code: ADHD_TAG_CODES[q.key] || q.key,
  label: `${ADHD_TAG_CODES[q.key] || q.key} — ${q.label}`,
}));

/** Where the evidence came from. The same three for both pathways. */
export const TAG_SOURCE_OPTIONS = AUTISM_TAG_SOURCE_OPTIONS;

const CRITERION_OPTIONS_BY_PATHWAY = {
  [PATHWAY.ASD]: AUTISM_TAG_CRITERION_OPTIONS,
  [PATHWAY.ADHD]: ADHD_TAG_CRITERION_OPTIONS,
};

/**
 * The criteria a clinician may tag against on this pathway.
 *
 * Returns an empty list for an unknown pathway rather than guessing: tagging
 * an answer against the wrong pathway's criteria would put a clinician's note
 * where they will never see it again.
 */
export function criterionOptionsFor(pathway) {
  return CRITERION_OPTIONS_BY_PATHWAY[pathway] || [];
}

/** The short code for a criterion key, for chips. Falls back to the key. */
export function criterionCodeFor(pathway, criterionKey) {
  const option = criterionOptionsFor(pathway).find((c) => c.key === criterionKey);
  return option?.code || criterionKey;
}

/** The full label for a criterion key. Falls back to the key. */
export function criterionLabelFor(pathway, criterionKey) {
  const option = criterionOptionsFor(pathway).find((c) => c.key === criterionKey);
  return option?.label || criterionKey;
}

const FACTOR_GROUPS_BY_PATHWAY = {
  [PATHWAY.ASD]: AUTISM_OTHER_FACTOR_GROUPS,
  [PATHWAY.ADHD]: OTHER_FACTOR_GROUPS,
};

/**
 * The other-factor groups an Other factor tag may name on this pathway, each
 * with its factors. Empty for an unknown pathway, for the same reason as
 * criterionOptionsFor.
 */
export function factorGroupsFor(pathway) {
  return FACTOR_GROUPS_BY_PATHWAY[pathway] || [];
}

/** The label of a factor group. Falls back to the key. */
export function factorGroupLabelFor(pathway, groupId) {
  return factorGroupsFor(pathway).find((g) => g.id === groupId)?.label || groupId;
}

/** The label of a specific factor within a group. Falls back to the key. */
export function factorLabelFor(pathway, groupId, factorKey) {
  const group = factorGroupsFor(pathway).find((g) => g.id === groupId);
  return group?.factors.find((f) => f.factorKey === factorKey)?.label || factorKey;
}

/**
 * The clinician's Other factor tags, grouped for the decision screen in the
 * pathway's group order (R1): within a group, the tags naming the group alone
 * come first, so a group-only tag always has a place, then the tags naming a
 * specific factor in the group's factor order. Groups with no tags are left
 * out: an untagged factor is never listed or prompted for (R2). A tag naming
 * a group the pathway does not have is dropped rather than misfiled.
 */
export function otherFactorTagsByGroup(pathway, tags) {
  const factorTags = tagsOfKind(tags, TAG_KIND.OTHER_FACTOR);
  return factorGroupsFor(pathway)
    .map((group) => {
      const inGroup = factorTags.filter((t) => t.factorGroup === group.id);
      const groupOnly = inGroup.filter((t) => !t.factorKey);
      const byFactor = group.factors.flatMap(
        (factor) => inGroup.filter((t) => t.factorKey === factor.factorKey),
      );
      return { group, tags: [...groupOnly, ...byFactor] };
    })
    .filter((entry) => entry.tags.length > 0);
}

/**
 * The short text a tag chip shows: the criterion's code for a criterion tag,
 * the factor (or the group alone) for an other-factor tag, and the kind's own
 * name for a risk or conclusion tag.
 */
export function tagChipLabel(pathway, tag) {
  const kind = tagKindOf(tag);
  if (kind === TAG_KIND.CRITERION) return criterionCodeFor(pathway, tag.criterionKey);
  if (kind === TAG_KIND.OTHER_FACTOR) {
    return tag.factorKey
      ? factorLabelFor(pathway, tag.factorGroup, tag.factorKey)
      : factorGroupLabelFor(pathway, tag.factorGroup);
  }
  if (kind === TAG_KIND.RISK) return 'Risk';
  return 'Conclusion';
}

/**
 * One stored tag as the decision payload carries it, so the decision records
 * exactly the tags (kinds, factors and notes included) the clinician made.
 */
export function decisionTagPayload(tag) {
  const kind = tagKindOf(tag);
  return {
    kind,
    criterion: kind === TAG_KIND.CRITERION ? tag.criterionKey : null,
    factorGroup: kind === TAG_KIND.OTHER_FACTOR ? tag.factorGroup || null : null,
    factorKey: kind === TAG_KIND.OTHER_FACTOR ? tag.factorKey || null : null,
    note: (tag.note || '').trim(),
    source: tag.source,
    // The server takes the source from the role when it is sent (TDI-393).
    ...(tag.role ? { role: tag.role } : {}),
    // The form the tagged answer is on: the letter says whether a school
    // answer was the SENCO's or the class teacher's (TDI-698).
    ...(tag.role && tag.formKey ? { formKey: tag.formKey } : {}),
    label: tag.label,
    value: tag.value,
    // The index of the tagged line within a multi-part answer, null for a
    // whole-answer tag. `value` already carries that line's text.
    partIndex: typeof tag.partIndex === 'number' ? tag.partIndex : null,
  };
}

/**
 * The evidence source for the respondent whose answer is being tagged. The
 * clinician never chooses it (TDI-393): the server sets the same value from
 * the role on every tag write.
 */
export const ROLE_DEFAULT_SOURCE = { guardian: 'home', school_user: 'school' };

/** The label for a tag's recorded source, for the tag form and chips. */
export function tagSourceLabel(source) {
  const option = TAG_SOURCE_OPTIONS.find((s) => s.id === source);
  return option ? option.label : null;
}
